import React, { useState, useEffect } from "react";
import { getAppointments, updateAppointment, deleteAppointment } from "../services/firestore";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

const TablePage = () => {
  const [appointments, setAppointments] = useState([]);
  const [deletedAppointments, setDeletedAppointments] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [filter, setFilter] = useState("");
  const [filterType, setFilterType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dobStartDate, setDobStartDate] = useState("");
  const [dobEndDate, setDobEndDate] = useState("");

  const fetchAppointments = async () => {
    try {
      const data = await getAppointments();
      setAppointments(data);
    } catch (error) {
      toast.error("Failed to fetch data!");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this appointment?");
    if (!confirmDelete) return;

    try {
      const appointmentToDelete = appointments.find((appt) => appt.id === id);
      setDeletedAppointments([...deletedAppointments, appointmentToDelete]); // Add to deleted list
      await deleteAppointment(id);
      toast.success("🎉 Appointment deleted successfully!", { autoClose: 3000 });
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to delete appointment!");
    }
  };

  const handleCompleteDelete = (index) => {
    const confirmDelete = window.confirm("Are you sure you want to permanently delete this entry?");
    if (!confirmDelete) return;

    const updatedDeletedAppointments = [...deletedAppointments];
    updatedDeletedAppointments.splice(index, 1); // Remove from deleted appointments
    setDeletedAppointments(updatedDeletedAppointments);
    toast.success("Entry permanently deleted!");
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateAppointment(id, { status });
      toast.success("Status updated!");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to update status!");
    }
  };

  const handleExport = () => {
    const exportData = filteredAppointments.map(({ id, ...rest }) => rest);

    let fileName = "appointments";
    if (filter) fileName += `_${filter}`;
    if (filterType) fileName += `_${filterType}`;
    if (startDate && endDate) fileName += `_date_${startDate}_to_${endDate}`;
    if (dobStartDate && dobEndDate) fileName += `_dob_${dobStartDate}_to_${dobEndDate}`;
    fileName += ".xlsx";

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Appointments");
    XLSX.writeFile(workbook, fileName.replaceAll(" ", "_"));
  };

  const filteredAppointments = appointments.filter((appointment) => {
    const matchesFilter = filter
      ? appointment.name?.toLowerCase().includes(filter.toLowerCase()) ||
        appointment.email?.toLowerCase().includes(filter.toLowerCase())
      : true;

    const matchesType = filterType ? appointment.lookingFor === filterType : true;

    const matchesDateRange =
      startDate && endDate
        ? new Date(appointment.todaysDate) >= new Date(startDate) &&
          new Date(appointment.todaysDate) <= new Date(endDate)
        : true;

    const matchesDobRange =
      dobStartDate && dobEndDate
        ? new Date(appointment.dob) >= new Date(dobStartDate) &&
          new Date(appointment.dob) <= new Date(dobEndDate)
        : true;

    return matchesFilter && matchesType && matchesDateRange && matchesDobRange;
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Appointments Table</h1>
        <Link to="/dashboard" className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
          Back to Dashboard
        </Link>
      </div>

      <div className="mb-6">
        <h3 className="font-semibold mb-2">Filters</h3>
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Search by Name or Email"
            className="p-2 border rounded w-full md:w-1/4"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="p-2 border rounded w-full md:w-1/4"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">Filter by Type</option>
            <option value="Residential">Residential</option>
            <option value="Commercial">Commercial</option>
          </select>
          <div>
            <label>Filter by Date Range:</label>
            <input
              type="date"
              className="p-2 border rounded ml-2"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <input
              type="date"
              className="p-2 border rounded ml-2"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label>Filter by DOB Range:</label>
            <input
              type="date"
              className="p-2 border rounded ml-2"
              value={dobStartDate}
              onChange={(e) => setDobStartDate(e.target.value)}
            />
            <input
              type="date"
              className="p-2 border rounded ml-2"
              value={dobEndDate}
              onChange={(e) => setDobEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <button
        onClick={handleExport}
        className="mb-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
      >
        Export Data
      </button>

      <button
        onClick={() => setShowDeleted(!showDeleted)}
        className="ml-4 mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
      >
        {showDeleted ? "Show Main Table" : "Show Deleted Entries"}
      </button>

      {!showDeleted ? (
        <table className="w-full bg-white border rounded-lg shadow-md overflow-hidden">
          <thead className="bg-gray-300 text-gray-800">
            <tr>
              <th className="p-4 border">Details</th>
              <th className="p-4 border">Date & Time</th>
              <th className="p-4 border">Type</th>
              <th className="p-4 border">Looking For</th>
              <th className="p-4 border">Notes</th>
              <th className="p-4 border">Status</th>
              <th className="p-4 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.map((appointment) => (
              <tr key={appointment.id} className="hover:bg-gray-100">
                <td className="p-4 border">
                  <div className="text-sm font-semibold">{appointment.name || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.email || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.phone || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.dob || "N/A"}</div>
                </td>
                <td className="p-4 border">
                  <div className="text-sm">{appointment.todaysDate || "N/A"}</div>
                  <div className="text-sm">{appointment.todaysTime || "N/A"}</div>
                </td>
                <td className="p-4 border">{appointment.type || "N/A"}</td>
                <td className="p-4 border">{appointment.lookingFor || "N/A"}</td>
                <td className="p-4 border">{appointment.notes || "N/A"}</td>
                <td className="p-4 border">
                  <select
                    className={`p-2 rounded ${
                      appointment.status === "Completed"
                        ? "bg-green-200"
                        : appointment.status === "Pending"
                        ? "bg-yellow-200"
                        : "bg-blue-200"
                    }`}
                    value={appointment.status}
                    onChange={(e) => handleStatusChange(appointment.id, e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="Rescheduled">Rescheduled</option>
                  </select>
                </td>
                <td className="p-4 border">
                  <button
                    onClick={() => handleDelete(appointment.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="w-full bg-white border rounded-lg shadow-md overflow-hidden">
          <thead className="bg-gray-300 text-gray-800">
            <tr>
              <th className="p-4 border">Details</th>
              <th className="p-4 border">Date & Time</th>
              <th className="p-4 border">Type</th>
              <th className="p-4 border">Looking For</th>
              <th className="p-4 border">Notes</th>
              <th className="p-4 border">Status</th>
              <th className="p-4 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deletedAppointments.map((appointment, index) => (
              <tr key={index} className="hover:bg-gray-100">
                <td className="p-4 border">
                  <div className="text-sm font-semibold">{appointment.name || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.email || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.phone || "N/A"}</div>
                  <div className="text-sm text-gray-600">{appointment.dob || "N/A"}</div>
                </td>
                <td className="p-4 border">
                  <div className="text-sm">{appointment.todaysDate || "N/A"}</div>
                  <div className="text-sm">{appointment.todaysTime || "N/A"}</div>
                </td>
                <td className="p-4 border">{appointment.type || "N/A"}</td>
                <td className="p-4 border">{appointment.lookingFor || "N/A"}</td>
                <td className="p-4 border">{appointment.notes || "N/A"}</td>
                <td className="p-4 border">{appointment.status || "N/A"}</td>
                <td className="p-4 border">
                  <button
                    onClick={() => handleCompleteDelete(index)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Permanently Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TablePage;
